/**
 * CommentItem Component
 * Task 5.5: Comment Thread View (Redesigned)
 *
 * Displays an individual comment with author info, content, and icon-based actions.
 * Uses PDS design tokens for consistent Pantheon branding.
 *
 * Features:
 * - Author avatar, name, and timestamp
 * - Markdown content rendering with @mention highlighting
 * - Icon-based actions in top-right:
 *   - Meatball menu (⋮): Copy link, Delete (author only)
 *   - Resolve checkmark (✓): Parent comments only
 *   - Jira ticket icon: Parent comments only
 * - Status indication (open/resolved)
 * - Full accessibility support
 *
 * Architecture:
 * - Pure presentational component
 * - Receives all data via props
 * - Actions delegated to parent via callbacks
 */

import React, { useState, useRef, useEffect } from 'react';
import type { Comment, JiraTicket } from '../../lib/types';
import { tokens as pdsTokens } from '../../styles/tokens';
import { MoreVerticalIcon, CheckIcon, RefreshIcon, JiraIcon } from '../icons/Icons';
import { TicketBadge } from '../jira/TicketBadge';
import { ShimmerBadge } from '../shared/Shimmer';

export interface CommentItemProps {
  comment: Comment;
  currentUserId?: string;
  resolvedByName?: string;
  /** Linked Jira ticket (if one exists for this comment) */
  linkedTicket?: JiraTicket;
  /** Whether the Jira form is currently open (hides Jira icon) */
  isJiraFormOpen?: boolean;
  /** Whether the Jira ticket is being loaded */
  isLoadingJiraTicket?: boolean;
  /** Whether actions should be disabled (e.g., during loading) */
  disabled?: boolean;
  onResolve?: (comment: Comment) => void;
  onReopen?: (comment: Comment) => void;
  onEdit?: (comment: Comment) => void;
  onDelete?: (comment: Comment) => void;
  onCopyLink?: (comment: Comment) => void;
  onMarkAsRead?: (comment: Comment) => void;
  onMarkAsUnread?: (comment: Comment) => void;
  onCreateJiraTicket?: (comment: Comment) => void;
  onOpenTicket?: (ticket: JiraTicket) => void;
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return 'just now';
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  } else if (diffDay < 7) {
    return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Format a date as full timestamp for tooltip
 */
function formatFullDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString();
}

/**
 * Get initials from name components
 * Uses givenName/familyName if available, otherwise parses full name
 */
function getInitials(givenName?: string, familyName?: string, fullName?: string): string {
  // If we have given and family name, use them directly
  if (givenName && familyName) {
    return (givenName[0] + familyName[0]).toUpperCase();
  }
  // If we only have given name, use first two letters
  if (givenName) {
    return givenName.slice(0, 2).toUpperCase();
  }
  // Fall back to parsing full name
  if (fullName) {
    return fullName
      .split(' ')
      .map((part) => part[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return '??';
}

/**
 * Render markdown content with @mention highlighting
 */
function renderContent(content: string): React.ReactNode {
  // Simple tokenizer - process content and replace patterns
  const tokens: { start: number; end: number; node: React.ReactNode }[] = [];
  let key = 0;

  // Process bold
  content.replace(/\*\*(.+?)\*\*/g, (match, text, offset) => {
    tokens.push({ start: offset, end: offset + match.length, node: <strong key={key++}>{text}</strong> });
    return match;
  });

  // Process italic (but not already bold)
  content.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, (match, text, offset) => {
    // Check if this overlaps with any existing tokens
    const overlaps = tokens.some(t =>
      (offset >= t.start && offset < t.end) ||
      (offset + match.length > t.start && offset + match.length <= t.end)
    );
    if (!overlaps) {
      tokens.push({ start: offset, end: offset + match.length, node: <em key={key++}>{text}</em> });
    }
    return match;
  });

  // Process code
  content.replace(/`(.+?)`/g, (match, text, offset) => {
    const overlaps = tokens.some(t =>
      (offset >= t.start && offset < t.end) ||
      (offset + match.length > t.start && offset + match.length <= t.end)
    );
    if (!overlaps) {
      tokens.push({ start: offset, end: offset + match.length, node: <code key={key++}>{text}</code> });
    }
    return match;
  });

  // Process @mentions
  content.replace(/@[\w.]+/g, (match, offset) => {
    const overlaps = tokens.some(t =>
      (offset >= t.start && offset < t.end) ||
      (offset + match.length > t.start && offset + match.length <= t.end)
    );
    if (!overlaps) {
      tokens.push({
        start: offset,
        end: offset + match.length,
        node: <span key={key++} data-mention="true" style={{ color: pdsTokens.colors.primary, fontWeight: pdsTokens.typography.fontWeight.medium }}>{match}</span>
      });
    }
    return match;
  });

  // Sort tokens by position
  tokens.sort((a, b) => a.start - b.start);

  // Build result
  if (tokens.length === 0) {
    return content;
  }

  const result: React.ReactNode[] = [];
  let lastEnd = 0;

  for (const token of tokens) {
    // Add text before this token
    if (token.start > lastEnd) {
      result.push(content.slice(lastEnd, token.start));
    }
    result.push(token.node);
    lastEnd = token.end;
  }

  // Add remaining text
  if (lastEnd < content.length) {
    result.push(content.slice(lastEnd));
  }

  return result;
}

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  currentUserId,
  resolvedByName,
  linkedTicket,
  isJiraFormOpen = false,
  isLoadingJiraTicket = false,
  disabled = false,
  onResolve,
  onReopen,
  onEdit,
  onDelete,
  onCopyLink,
  onMarkAsRead,
  onMarkAsUnread,
  onCreateJiraTicket,
  onOpenTicket,
}) => {
  const [showMeatballMenu, setShowMeatballMenu] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const meatballMenuRef = useRef<HTMLDivElement>(null);

  // Author info from comment.author (populated by API JOIN)
  const authorName = comment.author?.name || 'Unknown User';
  const authorGivenName = comment.author?.givenName;
  const authorFamilyName = comment.author?.familyName;
  const authorAvatar = comment.author?.picture || null;

  const isReply = comment.threadId !== null;
  const isOwnComment = currentUserId && comment.authorId === currentUserId;
  const isResolved = comment.status === 'resolved';
  const isParent = !isReply;

  // Check if meatball menu has any items to show
  // TODO: Re-integrate auth checks on deletion once MAS is in place
  // Delete should be restricted to comment author: (isOwnComment && onDelete)
  const hasMeatballMenuItems = !!(
    (isParent && (onMarkAsRead || onMarkAsUnread)) ||
    onCopyLink ||
    (isOwnComment && onEdit) ||
    onDelete
  );

  const handleResolve = () => {
    onResolve?.(comment);
  };

  const handleReopen = () => {
    onReopen?.(comment);
  };

  const handleEdit = () => {
    onEdit?.(comment);
    setShowMeatballMenu(false);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling to click-outside handler
    setShowDeleteConfirmation(true);
    setShowMeatballMenu(false);
  };

  const handleDeleteConfirm = () => {
    onDelete?.(comment);
    setShowDeleteConfirmation(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
  };

  const handleCopyLink = () => {
    onCopyLink?.(comment);
    setShowMeatballMenu(false);
  };

  const handleMarkAsRead = () => {
    onMarkAsRead?.(comment);
    setShowMeatballMenu(false);
  };

  const handleMarkAsUnread = () => {
    onMarkAsUnread?.(comment);
    setShowMeatballMenu(false);
  };

  const handleCreateJiraTicket = () => {
    onCreateJiraTicket?.(comment);
  };

  const toggleMeatballMenu = () => {
    setShowMeatballMenu(!showMeatballMenu);
  };

  // Close meatball menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isContained = meatballMenuRef.current?.contains(event.target as Node);
      if (meatballMenuRef.current && !isContained) {
        setShowMeatballMenu(false);
      }
    };

    if (showMeatballMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMeatballMenu]);

  return (
    <>
      <article
        data-testid={`comment-item-${comment.id}`}
        data-multidev-sdk="true"
        data-status={comment.status}
        data-is-reply={isReply ? 'true' : 'false'}
        role="article"
        aria-label={`Comment by ${authorName}, ${formatRelativeTime(comment.createdAt)}`}
        style={{
          padding: pdsTokens.spacing.s,
          borderRadius: pdsTokens.borders.radiusContainer,
          backgroundColor: isReply ? pdsTokens.colors.bgSecondary : pdsTokens.colors.bgDefault,
          border: `${pdsTokens.borders.widthDefault} solid ${pdsTokens.colors.borderDefault}`,
        }}
      >
      {/* Header: Avatar, Name, Timestamp, and Action Icons */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: pdsTokens.spacing['2xs'], marginBottom: pdsTokens.spacing['2xs'] }}>
        {/* Avatar */}
        {authorAvatar ? (
          <img
            src={authorAvatar}
            alt={authorName}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: pdsTokens.colors.primary,
              color: pdsTokens.colors.textReverse,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: pdsTokens.typography.fontSize.xs,
              fontWeight: pdsTokens.typography.fontWeight.semibold,
              flexShrink: 0,
            }}
          >
            {getInitials(authorGivenName, authorFamilyName, authorName)}
          </div>
        )}

        {/* Name and Timestamp */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: pdsTokens.typography.fontWeight.semibold, fontSize: pdsTokens.typography.fontSize.s, color: pdsTokens.colors.textDefault }}>
            {authorName}
          </div>
          <time
            dateTime={comment.createdAt}
            title={formatFullDate(comment.createdAt)}
            style={{ fontSize: pdsTokens.typography.fontSize.xs, color: pdsTokens.colors.textSecondary }}
          >
            {formatRelativeTime(comment.createdAt)}
          </time>
        </div>

        {/* Action Icons (Right-aligned) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: pdsTokens.spacing['3xs'], flexShrink: 0 }}>
          {/* Resolve/Reopen Icon - Parent comments only */}
          {isParent && (
            <>
              {!isResolved && onResolve && (
                <button
                  type="button"
                  onClick={handleResolve}
                  onMouseEnter={() => setHoveredButton('resolve')}
                  onMouseLeave={() => setHoveredButton(null)}
                  aria-label="Resolve comment"
                  data-testid="resolve-button"
                  title="Resolve"
                  disabled={disabled}
                  style={{
                    padding: pdsTokens.spacing['5xs'],
                    background: hoveredButton === 'resolve' ? pdsTokens.colors.gray100 : 'transparent',
                    border: 'none',
                    borderRadius: pdsTokens.borders.radiusButton,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    width: '28px',
                    height: '28px',
                    transition: 'background-color 0.15s ease',
                    opacity: disabled ? 0.5 : 1,
                  }}
                >
                  <CheckIcon size={16} color={pdsTokens.colors.gray400} />
                </button>
              )}
              {isResolved && onReopen && (
                <button
                  type="button"
                  onClick={handleReopen}
                  onMouseEnter={() => setHoveredButton('reopen')}
                  onMouseLeave={() => setHoveredButton(null)}
                  aria-label="Reopen comment"
                  data-testid="reopen-button"
                  title="Reopen"
                  style={{
                    padding: pdsTokens.spacing['5xs'],
                    background: hoveredButton === 'reopen' ? pdsTokens.colors.gray100 : 'transparent',
                    border: 'none',
                    borderRadius: pdsTokens.borders.radiusButton,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    width: '28px',
                    height: '28px',
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  <RefreshIcon size={16} color={pdsTokens.colors.gray400} />
                </button>
              )}
            </>
          )}

          {/* Jira Ticket Badge, Shimmer, or Icon - Parent comments only */}
          {isParent && (
            <>
              {/* Show shimmer while loading ticket */}
              {isLoadingJiraTicket && (
                <ShimmerBadge data-testid="jira-badge-shimmer" />
              )}

              {/* Show ticket badge if ticket exists and not loading */}
              {!isLoadingJiraTicket && linkedTicket && (
                <TicketBadge ticket={linkedTicket} onClick={onOpenTicket} />
              )}

              {/* Show Jira icon if no ticket, not loading, and form is not open */}
              {!isLoadingJiraTicket && !linkedTicket && !isJiraFormOpen && onCreateJiraTicket && (
                <button
                  type="button"
                  onClick={handleCreateJiraTicket}
                  onMouseEnter={() => setHoveredButton('jira')}
                  onMouseLeave={() => setHoveredButton(null)}
                  aria-label="Create Jira ticket"
                  data-testid="jira-button"
                  title="Create Jira ticket"
                  style={{
                    padding: pdsTokens.spacing['5xs'],
                    background: hoveredButton === 'jira' ? pdsTokens.colors.gray100 : 'transparent',
                    border: 'none',
                    borderRadius: pdsTokens.borders.radiusButton,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    width: '28px',
                    height: '28px',
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  <JiraIcon size={16} color={pdsTokens.colors.gray400} />
                </button>
              )}
            </>
          )}

          {/* Meatball Menu - Only shown when there are menu items */}
          {hasMeatballMenuItems && (
            <div style={{ position: 'relative' }} ref={meatballMenuRef}>
              <button
                type="button"
                onClick={toggleMeatballMenu}
                onMouseEnter={() => setHoveredButton('meatball')}
                onMouseLeave={() => setHoveredButton(null)}
                aria-label="Comment actions"
                aria-expanded={showMeatballMenu}
                data-testid="meatball-menu-button"
                style={{
                  padding: pdsTokens.spacing['5xs'],
                  background: hoveredButton === 'meatball' ? pdsTokens.colors.gray100 : 'transparent',
                  border: 'none',
                  borderRadius: pdsTokens.borders.radiusButton,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                  transition: 'background-color 0.15s ease',
                }}
              >
                <MoreVerticalIcon size={16} color={pdsTokens.colors.gray400} />
              </button>

            {/* Meatball Dropdown Menu */}
            {showMeatballMenu && (
              <div
                data-testid="meatball-menu"
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: pdsTokens.spacing['5xs'],
                  backgroundColor: pdsTokens.colors.bgDefault,
                  border: `${pdsTokens.borders.widthDefault} solid ${pdsTokens.colors.borderDefault}`,
                  borderRadius: pdsTokens.borders.radiusContainer,
                  boxShadow: pdsTokens.elevation.overlay,
                  zIndex: pdsTokens.zIndex.modal,
                  minWidth: '160px',
                }}
              >
                {/* Mark as Read/Unread - First item, parent comments only */}
                {isParent && comment.isUnread && onMarkAsRead && (
                  <button
                    type="button"
                    onClick={handleMarkAsRead}
                    onMouseDown={(e) => {
                      e.stopPropagation(); // Prevent click-outside from closing menu before onClick fires
                    }}
                    data-testid="mark-as-read-button"
                    style={{
                      width: '100%',
                      padding: `${pdsTokens.spacing['2xs']} ${pdsTokens.spacing.s}`,
                      background: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: pdsTokens.typography.fontSize.s,
                      color: pdsTokens.colors.textDefault,
                      display: 'block',
                    }}
                  >
                    Mark as read
                  </button>
                )}
                {isParent && !comment.isUnread && onMarkAsUnread && (
                  <button
                    type="button"
                    onClick={handleMarkAsUnread}
                    onMouseDown={(e) => {
                      e.stopPropagation(); // Prevent click-outside from closing menu before onClick fires
                    }}
                    data-testid="mark-as-unread-button"
                    style={{
                      width: '100%',
                      padding: `${pdsTokens.spacing['2xs']} ${pdsTokens.spacing.s}`,
                      background: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: pdsTokens.typography.fontSize.s,
                      color: pdsTokens.colors.textDefault,
                      display: 'block',
                    }}
                  >
                    Mark as unread
                  </button>
                )}
                {onCopyLink && (
                  <button
                    type="button"
                    onClick={() => {
                      handleCopyLink();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation(); // Prevent click-outside from closing menu before onClick fires
                    }}
                    data-testid="copy-link-button"
                    style={{
                      width: '100%',
                      padding: `${pdsTokens.spacing['2xs']} ${pdsTokens.spacing.s}`,
                      background: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: pdsTokens.typography.fontSize.s,
                      color: pdsTokens.colors.textDefault,
                      display: 'block',
                    }}
                  >
                    Copy link
                  </button>
                )}
                {isOwnComment && onEdit && (
                  <button
                    type="button"
                    onClick={handleEdit}
                    data-testid="edit-button"
                    style={{
                      width: '100%',
                      padding: `${pdsTokens.spacing['2xs']} ${pdsTokens.spacing.s}`,
                      background: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: pdsTokens.typography.fontSize.s,
                      color: pdsTokens.colors.textDefault,
                      display: 'block',
                    }}
                  >
                    Edit
                  </button>
                )}
                {/* TODO: Restrict to author only once MAS is in place: {isOwnComment && onDelete && ( */}
                {onDelete && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()} // Prevent mousedown from triggering click-outside
                    onClick={handleDelete}
                    data-testid="delete-button"
                    style={{
                      width: '100%',
                      padding: `${pdsTokens.spacing['2xs']} ${pdsTokens.spacing.s}`,
                      background: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: pdsTokens.typography.fontSize.s,
                      color: pdsTokens.colors.criticalFg,
                      display: 'block',
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ fontSize: pdsTokens.typography.fontSize.s, color: pdsTokens.colors.textDefault, lineHeight: pdsTokens.typography.lineHeight.m, marginBottom: pdsTokens.spacing['2xs'] }}>
        {renderContent(comment.content)}
      </div>

      {/* Resolved Info */}
      {isResolved && resolvedByName && (
        <div style={{ fontSize: pdsTokens.typography.fontSize.xs, color: pdsTokens.colors.successFg }}>
          Resolved by {resolvedByName}
        </div>
      )}
      </article>

      {/* Delete Confirmation - appears below comment box */}
      {showDeleteConfirmation && (
        <div
          style={{
            marginTop: pdsTokens.spacing.xs,
            paddingTop: pdsTokens.spacing.xs,
          }}
        >
          <div
            style={{
              fontSize: pdsTokens.typography.fontSize.s,
              color: pdsTokens.colors.criticalFg,
              lineHeight: pdsTokens.typography.lineHeight.m,
              marginBottom: pdsTokens.spacing.xs,
              fontWeight: pdsTokens.typography.fontWeight.medium,
            }}
          >
            {isReply
              ? 'Are you sure you want to delete this reply? This action cannot be undone.'
              : 'Are you sure you want to delete this comment and all its replies? This action cannot be undone.'}
          </div>
          <div style={{ display: 'flex', gap: pdsTokens.spacing.xs, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleDeleteCancel}
              data-testid="delete-cancel-button"
              style={{
                padding: `${pdsTokens.spacing['2xs']} ${pdsTokens.spacing.s}`,
                fontSize: pdsTokens.typography.fontSize.s,
                fontWeight: pdsTokens.typography.fontWeight.medium,
                color: pdsTokens.colors.textDefault,
                backgroundColor: pdsTokens.colors.bgDefault,
                border: `${pdsTokens.borders.widthDefault} solid ${pdsTokens.colors.borderDefault}`,
                borderRadius: pdsTokens.borders.radiusButton,
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = pdsTokens.colors.gray100;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = pdsTokens.colors.bgDefault;
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteConfirm}
              data-testid="delete-confirm-button"
              style={{
                padding: `${pdsTokens.spacing['2xs']} ${pdsTokens.spacing.s}`,
                fontSize: pdsTokens.typography.fontSize.s,
                fontWeight: pdsTokens.typography.fontWeight.medium,
                color: pdsTokens.colors.textReverse,
                backgroundColor: pdsTokens.colors.critical,
                border: 'none',
                borderRadius: pdsTokens.borders.radiusButton,
                cursor: 'pointer',
                transition: 'opacity 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </>
  );
};
